import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { getCapacitorPlatform, isPlatformIOS, isSafariBrowserApp } from 'environments/environment-helpers';
import { ScannerName, pickCameraPermissionHelpVariant } from 'services/camera-permission-help';
import { cameraPermissionSteps } from 'components/cs-scanner-permission-denied/camera-permission-steps';
import { CameraPermissionBlockService } from 'services/camera-permission-block.service';

/**
 * The card shown when the user has denied camera access: the
 * scanner-specific message plus the "how to switch the camera back on"
 * steps for wherever this build's camera permission actually lives.
 * All scanners show the steps — owner decision #11.
 *
 * The steps differ by more than the device make (#5139): inside the installed
 * app the switch is in the phone's own settings, while on the web it is a
 * browser control. A native user sent to the browser wording has nothing to
 * follow, which is what a customer reported.
 */
@Component({
  selector: 'cs-scanner-permission-denied',
  templateUrl: 'cs-scanner-permission-denied.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CsScannerPermissionDeniedComponent implements OnInit {
  protected LL = LL;

  /** Scanner-specific "camera access is needed because…" line, already translated. */
  @Input() message = '';

  /** Which scanner this card belongs to — recorded with the telemetry so we can
   *  see where users get blocked. */
  @Input({ required: true }) scanner!: ScannerName;

  private blockedCamera = inject(CameraPermissionBlockService);

  /** which set of steps this build shows; neither the platform nor the browser
   *  can change under us */
  private variant = pickCameraPermissionHelpVariant(
    getCapacitorPlatform(),
    isPlatformIOS(),
    isSafariBrowserApp(),
  );

  /** The steps, translated on each render so switching language re-renders them
   *  in the new one — the same reason `cs-scanner-hints` resolves its translation
   *  factories in the template rather than storing the resolved strings. */
  protected get steps(): string[] {
    return cameraPermissionSteps(this.variant);
  }

  ngOnInit(): void {
    this.blockedCamera.recordBlockedCardShown(this.scanner);
  }
}
